import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "availableList",
    "availableCount",
    "selectedAvailableCount",
    "selectedList",
    "hiddenInputs",
    "ordersCount",
    "stopsCount",
    "distanceEstimate",
    "durationEstimate",
    "form",
    "warehouseSelect",
    "vehicleSelect",
    "driverSelect",
    "returnToBaseInput",
    "returnModeInfo",
    "searchInput",
    "map",
    "scheduledDateInput",
    "assignmentFieldset",
    "assignmentHint",
    "assignmentOverlay",
    "ordersFieldset",
    "ordersHint",
    "ordersOverlay",
    "optimizeButton",
    "autoOptimizeInput",
    "batchAddButton",
    "selectVisibleInput",
    "submitButton",
    "submitButtonLabel",
    "submitSpinner",
    "submitOverlay",
    "reviewOverlay",
    "mapOverlay",
    "stepDate",
    "stepAssignment",
    "stepOrders",
    "stepReview"
  ]

  static values = {
    estimateUrl: String,
    optimizeUrl: String
  }

  connect() {
    this.selectedOrders = []
    this.estimateTimeout = null
    this.originLat = null
    this.originLng = null
    this.map = null
    this.routeLine = null
    this.markers = []
    this.isSubmitting = false

    this.updateOriginFromWarehouse()
    this.syncReturnToBaseFromVehicle()
    this.updateReturnModeLabel()
    this.initializeMap()
    this.refresh({ skipEstimate: true })
    this.calculateRouteEstimate()
  }

  disconnect() {
    clearTimeout(this.estimateTimeout)

    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  scheduledDateChanged() {
    this.refresh()
  }

  assignmentChanged() {
    this.refresh()
  }

  async warehouseChanged() {
    this.updateOriginFromWarehouse()
    const shouldAutoOptimize = this.autoOptimizeEnabled() && this.selectedOrders.length > 1

    this.refresh({ skipEstimate: shouldAutoOptimize })

    if (shouldAutoOptimize) {
      await this.maybeAutoOptimize()
    }
  }

  async vehicleChanged() {
    this.syncReturnToBaseFromVehicle()
    this.updateReturnModeLabel()
    const shouldAutoOptimize = this.autoOptimizeEnabled() && this.selectedOrders.length > 1

    this.refresh({ skipEstimate: shouldAutoOptimize })

    if (shouldAutoOptimize) {
      await this.maybeAutoOptimize()
    }
  }

  async returnToBaseChanged() {
    this.updateReturnModeLabel()
    const shouldAutoOptimize = this.autoOptimizeEnabled() && this.selectedOrders.length > 1

    this.refresh({ skipEstimate: shouldAutoOptimize })

    if (shouldAutoOptimize) {
      await this.maybeAutoOptimize()
    }
  }

  autoOptimizeChanged() {
    this.syncStepState()

    if (this.autoOptimizeEnabled()) {
      this.maybeAutoOptimize()
    }
  }

  updateOriginFromWarehouse() {
    if (!this.hasWarehouseSelectTarget) {
      this.originLat = null
      this.originLng = null
      return
    }

    const option = this.warehouseSelectTarget.selectedOptions[0]

    if (!option || !option.value) {
      this.originLat = null
      this.originLng = null
      return
    }

    const lat = parseFloat(option.dataset.latitude)
    const lng = parseFloat(option.dataset.longitude)

    this.originLat = Number.isFinite(lat) ? lat : null
    this.originLng = Number.isFinite(lng) ? lng : null
  }

  syncReturnToBaseFromVehicle() {
    if (!this.hasVehicleSelectTarget || !this.hasReturnToBaseInputTarget) return

    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) {
      this.returnToBaseInputTarget.checked = false
      return
    }

    this.returnToBaseInputTarget.checked = String(option.dataset.returnToBase) === "1"
  }

  updateReturnModeLabel() {
    const label = this.hasReturnToBaseInputTarget && this.returnToBaseInputTarget.checked
      ? "Volta para a base ao final da operacao"
      : "Encerra no ultimo ponto da sequencia"

    this.returnModeInfoTargets.forEach((target) => {
      target.textContent = label
    })
  }

  hasDateSelected() {
    return this.hasScheduledDateInputTarget && this.scheduledDateInputTarget.value.trim() !== ""
  }

  hasWarehouseSelected() {
    return this.hasWarehouseSelectTarget && this.warehouseSelectTarget.value.trim() !== ""
  }

  hasVehicleSelected() {
    return this.hasVehicleSelectTarget && this.vehicleSelectTarget.value.trim() !== ""
  }

  canSelectOrders() {
    return this.hasDateSelected() && this.hasWarehouseSelected() && this.hasVehicleSelected()
  }

  autoOptimizeEnabled() {
    return !this.hasAutoOptimizeInputTarget || this.autoOptimizeInputTarget.checked
  }

  selectedVehicleMaxStops() {
    if (!this.hasVehicleSelectTarget) return null

    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) return null

    const value = parseInt(option.dataset.maxSpots, 10)
    return Number.isFinite(value) ? value : null
  }

  selectedVehicleMaxDistanceKm() {
    if (!this.hasVehicleSelectTarget) return null

    const option = this.vehicleSelectTarget.selectedOptions[0]
    if (!option || !option.value) return null

    const value = parseFloat(option.dataset.maxDistanceKm)
    return Number.isFinite(value) ? value : null
  }

  initializeMap() {
    if (!this.hasMapTarget || typeof L === "undefined" || this.map) return

    const initialLat = Number.isFinite(this.originLat) ? this.originLat : -23.55052
    const initialLng = Number.isFinite(this.originLng) ? this.originLng : -46.63331

    this.map = L.map(this.mapTarget).setView([initialLat, initialLng], 11)

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors"
    }).addTo(this.map)

    if (Number.isFinite(this.originLat) && Number.isFinite(this.originLng)) {
      this.drawOriginOnly()
    }

    setTimeout(() => {
      if (this.map) this.map.invalidateSize()
    }, 200)
  }

  originIcon() {
    return L.divIcon({
      className: "custom-origin-marker",
      html: '<div class="marker-origin">O</div>',
      iconSize: [34, 34],
      iconAnchor: [17, 17]
    })
  }

  stopIcon(index) {
    return L.divIcon({
      className: "custom-numbered-marker",
      html: `<div class="marker-pin">${index + 1}</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    })
  }

  drawOriginOnly() {
    if (!this.map) return

    this.clearMapLayers()

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) return

    const originMarker = L.marker([this.originLat, this.originLng], {
      icon: this.originIcon()
    })
      .addTo(this.map)
      .bindPopup("Origem da rota")

    this.markers.push(originMarker)
    this.map.setView([this.originLat, this.originLng], 12)
  }

  clearMapLayers() {
    if (this.routeLine && this.map) {
      this.map.removeLayer(this.routeLine)
      this.routeLine = null
    }

    this.markers.forEach((marker) => {
      if (this.map) this.map.removeLayer(marker)
    })
    this.markers = []
  }

  drawRoute(geometry) {
    if (!this.map || !geometry || !Array.isArray(geometry.coordinates)) return

    this.clearMapLayers()

    const latLngs = geometry.coordinates.map((coord) => [coord[1], coord[0]])

    this.routeLine = L.polyline(latLngs, {
      weight: 5,
      opacity: 0.85
    }).addTo(this.map)

    if (Number.isFinite(this.originLat) && Number.isFinite(this.originLng)) {
      const originMarker = L.marker([this.originLat, this.originLng], {
        icon: this.originIcon()
      })
        .addTo(this.map)
        .bindPopup("Origem da rota")

      this.markers.push(originMarker)
    }

    this.selectedOrders.forEach((order, index) => {
      if (!Number.isFinite(order.latitude) || !Number.isFinite(order.longitude)) return

      const marker = L.marker([order.latitude, order.longitude], {
        icon: this.stopIcon(index)
      })
        .addTo(this.map)
        .bindPopup(`Parada ${index + 1} - Pedido #${order.id}`)

      this.markers.push(marker)
    })

    if (this.routeLine.getBounds().isValid()) {
      this.map.fitBounds(this.routeLine.getBounds(), { padding: [30, 30] })
    }
  }

  visibleAvailableCards() {
    if (!this.hasAvailableListTarget) return []

    return Array.from(this.availableListTarget.querySelectorAll(".order-card")).filter((card) => {
      return card.style.display !== "none"
    })
  }

  checkedAvailableCards() {
    if (!this.hasAvailableListTarget) return []

    return Array.from(
      this.availableListTarget.querySelectorAll(".order-card .order-selector:checked")
    ).map((checkbox) => checkbox.closest(".order-card")).filter(Boolean)
  }

  availableSelectionChanged(event) {
    const card = event.currentTarget.closest(".order-card")
    if (!card) return

    this.updateOrderCardSelectionState(card)
    this.updateBatchSelectionUI()
  }

  toggleVisibleSelection(event) {
    const checked = event.currentTarget.checked

    this.visibleAvailableCards().forEach((card) => {
      const checkbox = card.querySelector(".order-selector")
      if (!checkbox || checkbox.disabled) return

      checkbox.checked = checked
      this.updateOrderCardSelectionState(card)
    })

    this.updateBatchSelectionUI()
  }

  updateOrderCardSelectionState(card) {
    const checkbox = card.querySelector(".order-selector")
    card.classList.toggle("is-checked", !!checkbox && checkbox.checked)
  }

  updateBatchSelectionUI() {
    const checkedCount = this.checkedAvailableCards().length

    if (this.hasSelectedAvailableCountTarget) {
      this.selectedAvailableCountTarget.textContent = checkedCount
    }

    if (this.hasBatchAddButtonTarget) {
      this.batchAddButtonTarget.disabled = !this.canSelectOrders() || checkedCount === 0
    }

    if (this.hasSelectVisibleInputTarget) {
      const visibleCheckboxes = this.visibleAvailableCards()
        .map((card) => card.querySelector(".order-selector"))
        .filter((checkbox) => checkbox && !checkbox.disabled)

      const allVisibleChecked = visibleCheckboxes.length > 0 &&
        visibleCheckboxes.every((checkbox) => checkbox.checked)

      this.selectVisibleInputTarget.checked = allVisibleChecked
      this.selectVisibleInputTarget.disabled = !this.canSelectOrders() || visibleCheckboxes.length === 0
    }
  }

  buildSelectedOrderFromCard(card) {
    const latitude = parseFloat(card.dataset.latitude)
    const longitude = parseFloat(card.dataset.longitude)

    return {
      id: card.dataset.orderId,
      customerName: card.dataset.customerName || "",
      addressLine: card.dataset.addressLine || "",
      warehouseId: card.dataset.warehouseId || null,
      latitude,
      longitude,
      availableHtml: card.outerHTML
    }
  }

  addOrderFromCard(card) {
    if (!card) return false

    const orderId = card.dataset.orderId
    if (!orderId) return false

    if (this.selectedOrders.find((item) => item.id === orderId)) return false

    const latitude = parseFloat(card.dataset.latitude)
    const longitude = parseFloat(card.dataset.longitude)

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      alert("Este pedido nao possui latitude/longitude valida para calculo da rota.")
      return false
    }

    this.selectedOrders.push(this.buildSelectedOrderFromCard(card))
    card.remove()

    return true
  }

  async addCheckedOrders() {
    const cards = this.checkedAvailableCards()

    if (cards.length === 0) {
      alert("Marque pelo menos um pedido para adicionar.")
      return
    }

    const maxStops = this.selectedVehicleMaxStops()
    let added = 0

    cards.forEach((card) => {
      if (maxStops && this.selectedOrders.length >= maxStops) return
      if (this.addOrderFromCard(card)) added += 1
    })

    if (maxStops && added < cards.length) {
      alert(`Este veiculo permite no maximo ${maxStops} paradas.`)
    }

    const shouldAutoOptimize = this.autoOptimizeEnabled() && this.selectedOrders.length > 1

    this.refresh({ skipEstimate: shouldAutoOptimize })

    if (shouldAutoOptimize) {
      await this.maybeAutoOptimize()
    }
  }

  restoreAvailableCard(item) {
    if (!this.hasAvailableListTarget || !item?.availableHtml) return

    const template = document.createElement("template")
    template.innerHTML = item.availableHtml.trim()

    const card = template.content.firstElementChild
    if (!card) return

    const checkbox = card.querySelector(".order-selector")
    if (checkbox) checkbox.checked = false

    card.classList.remove("is-checked")
    this.availableListTarget.appendChild(card)
  }

  async removeOrder(event) {
    const orderId = event.currentTarget.dataset.orderId
    if (!orderId) return

    const item = this.selectedOrders.find((order) => order.id === orderId)
    this.selectedOrders = this.selectedOrders.filter((order) => order.id !== orderId)

    this.restoreAvailableCard(item)
    const shouldAutoOptimize = this.autoOptimizeEnabled() && this.selectedOrders.length > 1

    this.refresh({ skipEstimate: shouldAutoOptimize })

    if (shouldAutoOptimize) {
      await this.maybeAutoOptimize()
    }
  }

  filterOrders() {
    if (!this.hasAvailableListTarget) return

    const term = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ""
    const selectedWarehouseId = this.hasWarehouseSelectTarget ? this.warehouseSelectTarget.value : ""

    this.availableListTarget.querySelectorAll(".order-card").forEach((card) => {
      const text = (card.dataset.searchText || "").toLowerCase()
      const cardWarehouseId = card.dataset.warehouseId || ""
      const matchesText = text.includes(term)
      const matchesWarehouse = !selectedWarehouseId || String(cardWarehouseId) === String(selectedWarehouseId)

      card.style.display = matchesText && matchesWarehouse ? "" : "none"
      this.updateOrderCardSelectionState(card)
    })

    this.updateAvailableCount()
    this.updateBatchSelectionUI()
  }

  updateAvailableCount() {
    if (!this.hasAvailableListTarget || !this.hasAvailableCountTarget) return

    const visibleCards = this.visibleAvailableCards()
    this.availableCountTarget.textContent = visibleCards.length
  }

  refresh(options = {}) {
    this.renderSelected()
    this.renderHiddenInputs()
    this.updateCounters()
    this.filterOrders()
    this.syncStepState()

    clearTimeout(this.estimateTimeout)

    if (options.skipEstimate) return

    this.estimateTimeout = setTimeout(() => {
      this.calculateRouteEstimate()
    }, 250)
  }

  renderSelected() {
    if (!this.hasSelectedListTarget) return

    this.selectedListTarget.innerHTML = ""

    if (this.selectedOrders.length === 0) {
      this.selectedListTarget.innerHTML = `
        <div class="text-muted text-center py-5">
          Nenhum pedido adicionado a rota.
        </div>
      `
      return
    }

    this.selectedOrders.forEach((item, index) => {
      const wrapper = document.createElement("div")
      wrapper.className = "selected-route-order mb-2"
      wrapper.innerHTML = `
        <div class="card-body py-3 px-3">
          <div class="d-flex align-items-start gap-3">
            <div class="route-sequence-badge">${index + 1}</div>

            <div class="flex-grow-1">
              <div class="fw-semibold">Pedido #${this.escapeHtml(item.id)}</div>
              <div>${this.escapeHtml(item.customerName || "Sem identificacao")}</div>
              <div class="text-muted small">${this.escapeHtml(item.addressLine || "Endereco nao informado")}</div>
            </div>

            <div class="d-flex flex-column gap-1">
              <button type="button"
                      class="btn btn-sm btn-light"
                      data-index="${index}"
                      data-action="click->routes#moveUp"
                      ${index === 0 ? "disabled" : ""}>
                ↑
              </button>

              <button type="button"
                      class="btn btn-sm btn-light"
                      data-index="${index}"
                      data-action="click->routes#moveDown"
                      ${index === this.selectedOrders.length - 1 ? "disabled" : ""}>
                ↓
              </button>

              <button type="button"
                      class="btn btn-sm btn-outline-danger"
                      data-order-id="${this.escapeHtml(item.id)}"
                      data-action="click->routes#removeOrder">
                Remover
              </button>
            </div>
          </div>
        </div>
      `

      this.selectedListTarget.appendChild(wrapper)
    })
  }

  renderHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return

    this.hiddenInputsTarget.innerHTML = ""

    this.selectedOrders.forEach((item) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "order_ids[]"
      input.value = item.id
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  updateCounters() {
    const total = this.selectedOrders.length

    if (this.hasOrdersCountTarget) {
      this.ordersCountTarget.textContent = total
    }

    if (this.hasStopsCountTarget) {
      this.stopsCountTarget.textContent = total
    }

    if (this.hasDistanceEstimateTarget) {
      this.distanceEstimateTarget.textContent = total > 0 ? "Calculando..." : "-"
    }

    if (this.hasDurationEstimateTarget) {
      this.durationEstimateTarget.textContent = total > 0 ? "Calculando..." : "00:00"
    }
  }

  syncStepState() {
    const hasDate = this.hasDateSelected()
    const hasAssignment = this.hasWarehouseSelected() && this.hasVehicleSelected()
    const canPickOrders = this.canSelectOrders()
    const hasOrders = this.selectedOrders.length > 0
    const reviewUnlocked = canPickOrders && hasOrders

    if (this.hasAssignmentFieldsetTarget) {
      this.assignmentFieldsetTarget.disabled = !hasDate
    }

    if (this.hasOrdersFieldsetTarget) {
      this.ordersFieldsetTarget.disabled = !canPickOrders
    }

    if (this.hasAssignmentHintTarget) {
      this.assignmentHintTarget.classList.toggle("d-none", hasDate)
    }

    if (this.hasOrdersHintTarget) {
      this.ordersHintTarget.classList.toggle("d-none", canPickOrders)
    }

    if (this.hasOptimizeButtonTarget) {
      this.optimizeButtonTarget.disabled = !canPickOrders || this.selectedOrders.length <= 1
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !canPickOrders || !hasOrders || this.isSubmitting
    }

    this.toggleOverlay(this.hasAssignmentOverlayTarget ? this.assignmentOverlayTarget : null, !hasDate)
    this.toggleOverlay(this.hasOrdersOverlayTarget ? this.ordersOverlayTarget : null, !canPickOrders)
    this.toggleOverlay(this.hasReviewOverlayTarget ? this.reviewOverlayTarget : null, !reviewUnlocked)
    this.toggleOverlay(this.hasMapOverlayTarget ? this.mapOverlayTarget : null, !reviewUnlocked)

    this.setStepState(this.stepDateTarget, hasDate ? "done" : "active")
    this.setStepState(this.stepAssignmentTarget, !hasDate ? "locked" : (hasAssignment ? "done" : "active"))
    this.setStepState(this.stepOrdersTarget, !canPickOrders ? "locked" : (hasOrders ? "done" : "active"))
    this.setStepState(this.stepReviewTarget, !reviewUnlocked ? "locked" : "active")
  }

  setStepState(target, state) {
    if (!target) return
    target.dataset.state = state
  }

  toggleOverlay(target, visible) {
    if (!target) return
    target.classList.toggle("d-none", !visible)
  }

  buildPayload(stops) {
    return {
      origin: {
        latitude: this.originLat,
        longitude: this.originLng
      },
      return_to_base: this.hasReturnToBaseInputTarget
        ? this.returnToBaseInputTarget.checked
        : false,
      stops: stops.map((order) => ({
        id: order.id,
        latitude: order.latitude,
        longitude: order.longitude
      }))
    }
  }

  async calculateRouteEstimate() {
    if (!this.hasDistanceEstimateTarget) return

    if (this.selectedOrders.length === 0) {
      this.distanceEstimateTarget.textContent = "-"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      this.drawOriginOnly()
      return
    }

    if (!this.hasEstimateUrlValue || !this.estimateUrlValue) {
      this.distanceEstimateTarget.textContent = "-"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      return
    }

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) {
      this.distanceEstimateTarget.textContent = "Origem pendente"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      this.drawOriginOnly()
      return
    }

    const validStops = this.selectedOrders.filter((order) => {
      return Number.isFinite(order.latitude) && Number.isFinite(order.longitude)
    })

    if (validStops.length === 0) {
      this.distanceEstimateTarget.textContent = "-"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
      this.drawOriginOnly()
      return
    }

    const payload = this.buildPayload(validStops)

    try {
      const response = await fetch(this.estimateUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`)
      }

      const data = await response.json()

      if (this.hasDistanceEstimateTarget) {
        this.distanceEstimateTarget.textContent =
          data.distance_km != null ? `${data.distance_km} km` : "-"
      }

      if (this.hasDurationEstimateTarget) {
        this.durationEstimateTarget.textContent = data.duration_text || "00:00"
      }

      const maxDistanceKm = this.selectedVehicleMaxDistanceKm()
      if (maxDistanceKm && data.distance_km && Number(data.distance_km) > maxDistanceKm) {
        this.distanceEstimateTarget.textContent = `${data.distance_km} km ⚠`
      }

      if (data.geometry) {
        this.drawRoute(data.geometry)
      } else {
        this.drawOriginOnly()
      }
    } catch (_error) {
      this.distanceEstimateTarget.textContent = "Erro"
      if (this.hasDurationEstimateTarget) this.durationEstimateTarget.textContent = "00:00"
    }
  }

  async optimize(event) {
    await this.runOptimization({ button: event?.currentTarget, silent: false })
  }

  async maybeAutoOptimize() {
    if (!this.autoOptimizeEnabled() || this.selectedOrders.length <= 1) return
    await this.runOptimization({ button: this.optimizeButtonTarget, silent: true })
  }

  async runOptimization({ button, silent }) {
    if (this.selectedOrders.length <= 1) {
      if (!silent) alert("Adicione pelo menos 2 pedidos para otimizar a sequencia.")
      return
    }

    if (!this.hasOptimizeUrlValue || !this.optimizeUrlValue) {
      if (!silent) alert("Endpoint de otimizacao nao configurado.")
      return
    }

    if (!Number.isFinite(this.originLat) || !Number.isFinite(this.originLng)) {
      if (!silent) alert("Selecione uma base de saida valida.")
      return
    }

    const actionButton = button || (this.hasOptimizeButtonTarget ? this.optimizeButtonTarget : null)
    const originalText = actionButton ? actionButton.innerHTML : null
    const payload = this.buildPayload(this.selectedOrders)

    try {
      if (actionButton) {
        actionButton.disabled = true
        actionButton.innerHTML = "Otimizando..."
      }

      const response = await fetch(this.optimizeUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`Erro HTTP ${response.status}`)
      }

      const data = await response.json()

      if (!Array.isArray(data.ordered_ids) || data.ordered_ids.length === 0) {
        throw new Error("OSRM nao retornou ordem otimizada")
      }

      const reordered = []

      data.ordered_ids.forEach((id) => {
        const found = this.selectedOrders.find((order) => String(order.id) === String(id))
        if (found) reordered.push(found)
      })

      if (reordered.length === this.selectedOrders.length) {
        this.selectedOrders = reordered
      }

      this.refresh({ skipEstimate: true })
      await this.calculateRouteEstimate()
    } catch (_error) {
      if (!silent) {
        alert("Nao foi possivel otimizar a sequencia da rota.")
      }
    } finally {
      if (actionButton) {
        actionButton.innerHTML = originalText
      }

      this.syncStepState()
    }
  }

  moveUp(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (!Number.isFinite(index) || index <= 0) return

    ;[this.selectedOrders[index - 1], this.selectedOrders[index]] = [
      this.selectedOrders[index],
      this.selectedOrders[index - 1]
    ]

    this.refresh()
  }

  moveDown(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (!Number.isFinite(index) || index >= this.selectedOrders.length - 1) return

    ;[this.selectedOrders[index], this.selectedOrders[index + 1]] = [
      this.selectedOrders[index + 1],
      this.selectedOrders[index]
    ]

    this.refresh()
  }

  submitForm(event) {
    if (this.isSubmitting) {
      event.preventDefault()
      return
    }

    if (!this.formTarget.checkValidity()) {
      event.preventDefault()
      this.formTarget.reportValidity()
      return
    }

    if (this.selectedOrders.length === 0) {
      event.preventDefault()
      alert("Adicione pelo menos um pedido antes de criar a rota.")
      return
    }

    this.isSubmitting = true

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    if (this.hasSubmitSpinnerTarget) {
      this.submitSpinnerTarget.classList.remove("d-none")
    }

    if (this.hasSubmitButtonLabelTarget) {
      this.submitButtonLabelTarget.textContent = "Criando rota..."
    }

    if (this.hasSubmitOverlayTarget) {
      this.submitOverlayTarget.classList.add("is-visible")
    }

    this.syncStepState()
  }

  jsonHeaders() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": csrfToken
    }
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
